import { Adresse, Vermittlernummer } from '@ps20/bff-models';
import {
  App,
  FEVorvertragsnummer,
  Leistung,
  LeistungEigenschaft,
  Produktlinie,
  ZusatzoptionItem,
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  SDWAbsprungData,
} from '@ps20-frontend/shop-integration-redux';
import { call, cancel, select, takeEvery } from 'redux-saga/effects';
import { convertDateFormatFromLocalStateToShortIso } from '@ps20-frontend/utils';
import { IAppState, Vermittler, Versicherungsnehmer } from '../appState';
import { UnfFachschluessel } from '../model/unf.enums';
import { Vp } from '../model/vp';
import { selectSelectedProduktlinie } from '../selector/produkt.selector';
import { UnfActions } from '../unfActions';
import { selectAngebotsId, selectVorvertragsNummern } from '../selector/kus-absprung.selector';

interface KusPayload {
  angebotsId: string;
  amisSessionId: string;
  sdw: SDWAbsprungData;
  vnKdvId: Versicherungsnehmer['kundenverwaltungID'];
  isErsatzgeschaeft: boolean;
  vorvertragsnummern: FEVorvertragsnummer['vertragsnummer'][];
  vp: {
    anrede: Vp['anrede'];
    vorname: Vp['nachname'];
    nachname: Vp['nachname'];
    geburtsdatum: string;
    kdvId: Vp['kundenverwaltungID'];
    absId: Vp['kundennummer'];
    berufsrisikogruppe: Vp['berufsrisikogruppe'];
    adresse: Adresse;
  };
  vermittlernummer: Omit<Vermittlernummer, 'vermittlerFom'>;
  bnrb: Vermittler['bnrb'];
  isFremdvermittler: Vermittler['isFremdvermittler'];
  produktName: Produktlinie['produktName'];
  leistungen: KusLeistung[];
}

interface KusLeistung {
  name: Leistung['name'];
  fachschluessel: Leistung['fachschluessel'];
  selected: boolean;
  betrag: string | null;
}

const LeistungWhitelist = new Set([
  UnfFachschluessel.LEISTUNG_AB,
  UnfFachschluessel.INV_SUMME,
  UnfFachschluessel.TOPSCHUTZ,
  UnfFachschluessel.TODESFALL,
  UnfFachschluessel.MONATLICHEUNFALLRENTE,
  UnfFachschluessel.KRANKENHAUSTAGEGELD,
  UnfFachschluessel.RUNDUMSERVICE,
  UnfFachschluessel.AKKUTLEISTUNG,
]);

export function* watchKusAbsprung(): Generator {
  yield takeEvery(UnfActions.GOTOKUS, safeSaga(kusAbsprungVorbereiten, App.UNF));
  yield takeEvery(UnfActions.GOTOKUS_SUCCESSFUL, safeSaga(kusAbsprungAusfuehren, App.UNF));
}

export function* kusAbsprungVorbereiten(): Generator {
  try {
    const state: IAppState = (yield select()) as IAppState;
    const globalPut = GlobalPut.getInstance();
    const payload = createPayload(state);
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().doRequest({
        appId: App.UNF,
        angebotsId: state.angebotsId,
        url: 'privatschutz/api/openkus',
        body: payload,
        successEvent: UnfActions.GOTOKUS_SUCCESSFUL,
        errorEvent: UnfActions.GOTOKUS_FAILED,
        noCache: true,
      }),
    );
  } catch (e) {
    PS20Logger.error(App.UNF, e as Error, 'absprungKusSaga.kusAbsprungVorbereiten()');
    yield cancel();
  }
}

const createPayload = (state: IAppState): KusPayload => {
  const produktlinie = selectSelectedProduktlinie(state);
  const vermittler = state.vermittler;
  const vermittlernummer = vermittler.isFremdvermittler
    ? vermittler.selectedVermittlernummer.fremd
    : vermittler.selectedVermittlernummer.eigen;
  const {
    anrede,
    vorname,
    nachname,
    geburtsdatum,
    kundenverwaltungID,
    kundennummer,
    berufsrisikogruppe,
    adresse,
  } = state.vp;
  return {
    angebotsId: state.angebotsId,
    sdw: state.transientState.sdw,
    amisSessionId: state.transientState.amisSessionId,
    vnKdvId: state.versicherungsnehmer.kundenverwaltungID,
    isErsatzgeschaeft: selectVorvertragsNummern(state).length !== 0,
    vorvertragsnummern: state.vertragsoptionen.vorvertragsNummern
      ?.map(vv => vv.vertragsnummer)
      .filter(Boolean),
    vp: {
      anrede,
      vorname,
      nachname,
      geburtsdatum: convertDateFormatFromLocalStateToShortIso(geburtsdatum),
      kdvId: kundenverwaltungID || undefined,
      absId: kundennummer || undefined,
      berufsrisikogruppe,
      adresse: {
        plz: adresse.plz,
        ort: adresse.ort,
        strasse: adresse.strasse,
        hausnummer: adresse.hausnummer,
        land: adresse.land,
      },
    },
    vermittlernummer: {
      geschaeftsstelle: '',
      vertreternummer: '',
      zweigniederlassung: '',
      ...vermittlernummer,
    },
    bnrb: state.vermittler.bnrb,
    isFremdvermittler: vermittler.isFremdvermittler || false,
    produktName: produktlinie.produktName,
    leistungen: [
      ...convertLeistungToKusLeistung(produktlinie.leistungen),
      ...convertZusatzoptionToKusLeistung(produktlinie.zusatzoptionen),
    ].filter(leistung => LeistungWhitelist.has(leistung.fachschluessel as UnfFachschluessel)),
  };
};

const convertLeistungToKusLeistung = (leistungen: Leistung[]): KusLeistung[] => {
  return leistungen.map(leistung => {
    const isBeitragANumber = !Number.isNaN(Number(leistung.eigenschaft));
    const selected: boolean =
      leistung.eigenschaft === LeistungEigenschaft.INKLUSIVE || isBeitragANumber;
    return {
      name: leistung.name,
      fachschluessel: leistung.fachschluessel,
      selected,
      betrag: isBeitragANumber ? (leistung.eigenschaft as string) : null,
    };
  });
};

const convertZusatzoptionToKusLeistung = (optionen: ZusatzoptionItem[]): KusLeistung[] => {
  return optionen.map(option => ({
    name: option.name,
    fachschluessel: option.fachschluessel,
    selected: option.isSelected,
    betrag: option.eigenschaft,
  }));
};

export function* kusAbsprungAusfuehren(
  action: DoRequestResponse<{ redirectUrl: string }>,
): Generator {
  try {
    const angebotsIdInStore = yield select(selectAngebotsId);
    const payload = action.doRequestAction.payload;

    if (payload.appId === App.UNF && payload.angebotsId === angebotsIdInStore) {
      window.open(action.payload.redirectUrl, '_blank', 'noopener, noreferrer');
    }
  } catch (e) {
    PS20Logger.error(App.UNF, e as Error, 'absprungKusSaga.kusAbsprungAusfuehren()');
    yield cancel();
  }
}
